import {useState, useEffect} from 'react'
import createStore, {PureStore} from 'pure-store'

export interface PureStoreHook<S> {
  // The actual state object. This changes every update so this store can be
  // passed down through components and will trigger re-renders as expected.
  state: S
  // A passthrough to `store.update`
  update: PureStore<S, S>['update']
  // A wrapper around the purestore's subscribe that feeds the current state at
  // the time of the update to the callback. It also returns void when called so
  // it can be fed directly to useEffect.
  subscribe: (callback: Callback<S>) => () => void
  // The full purestore instance.
  store: PureStore<S, S>
}

type Callback<S> = (state: S) => void

/**
 * useStore is a helper hook that returns an object eases some pain of using
 * pure-store inside functional components it exposes `{state, update,
 * subscribe, store}` which lets you use the result as if it were a pure store
 * directly.
 *
 * @export
 * @template S The initial state for the store
 * @param {S} initial
 * @returns {PureStoreHook<S>}
 */
export default function useStore<S>(initial: S): PureStoreHook<S> {
  const [store] = useState(createStore(initial))
  const [state, setState] = useState(initial)
  const [update] = useState(() => store.update.bind(store))
  const [subscribe] = useState(() => onSubscribe)

  useEffect(() => subscribe(setState), [subscribe, store])

  function onSubscribe(callback: Callback<S>): () => void {
    const unsubscribe = store.subscribe(() => callback(store.getState()))
    return () => {
      unsubscribe()
    }
  }

  return {
    state,
    subscribe,
    store,
    update
  }
}
